import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { NotificationBell } from "@/components/NotificationBell";
import { toast } from "sonner";
import { Select, SearchSelect, MultiSelect, CheckGroup } from "@/components/FormControls";
import { isValidUrl } from "@/lib/validators";
import {
  LogOut, Search, Sparkles, Bookmark, Briefcase, MessageSquare, CreditCard,
  ArrowRight, Loader2, BadgeCheck, Lock, Building2, Globe2, Users, AlertTriangle, Download,
  CalendarClock, Share2, SearchX, ArrowLeft, UserPlus, Mail, Trash2, Crown,
} from "lucide-react";

const TABS = [
  ["search", "Search", Search], ["team", "Assemble a Team", Users], ["saved", "Saved", Bookmark],
  ["roles", "Roles", Briefcase], ["messages", "Messages", MessageSquare],
  ["interviews", "Interviews", CalendarClock],
  ["mobility", "Mobility", Globe2], ["members", "Team", UserPlus], ["billing", "Access & Billing", CreditCard],
];

export default function EmployerDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [company, setCompany] = useState(undefined);
  const [access, setAccess] = useState(null);
  const [tab, setTab] = useState("search");

  const load = () => api.get("/company/mine").then((r) => { setCompany(r.data.company); setAccess(r.data.access); });
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const h = () => setTab("billing");
    window.addEventListener("ta:goto-billing", h);
    return () => window.removeEventListener("ta:goto-billing", h);
  }, []);

  const renewCheckout = async (plan) => {
    try {
      const { data } = await api.post("/payments/checkout", { plan, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (company === undefined) return <div className="min-h-screen grid place-items-center text-muted font-mono text-sm">Loading workspace…</div>;

  return (
    <div className="min-h-screen">
      <header className="ta-glass border-b border-white/10 sticky top-0 z-40">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 h-16 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-4">
            {access && <AccessBadge access={access} />}
            <NotificationBell />
            <span className="text-sm text-muted hidden sm:block">{user?.name}</span>
            <button onClick={() => { logout(); nav("/"); }} className="text-muted hover:text-signal transition-colors duration-200"><LogOut size={18} /></button>
          </div>
        </div>
      </header>

      {!company ? <CompanyRegister onDone={load} />
        : company.approval_status !== "approved" ? <PendingApproval company={company} />
        : (
          <div className="mx-auto max-w-7xl px-5 lg:px-8 py-8">
            <ExpiryBanner access={access} onRenew={() => renewCheckout("annual")} />
            <div className="flex gap-2 overflow-x-auto border-b border-white/10 mb-8">
              {TABS.map(([k, l, Icon]) => (
                <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
                  className={`flex items-center gap-2 px-4 py-3 text-sm whitespace-nowrap border-b-2 transition-colors duration-200 ${tab === k ? "border-azure text-ink" : "border-transparent text-muted hover:text-ink"}`}>
                  <Icon size={15} /> {l}
                </button>
              ))}
            </div>
            {tab === "search" && <SearchTab access={access} nav={nav} setTab={setTab} />}
            {tab === "team" && <TeamTab access={access} nav={nav} />}
            {tab === "saved" && <SavedTab nav={nav} access={access} />}
            {tab === "roles" && <RolesTab access={access} setTab={setTab} />}
            {tab === "messages" && <MessagesTab />}
            {tab === "interviews" && <InterviewsTab access={access} />}
            {tab === "mobility" && <MobilityTab access={access} />}
            {tab === "members" && <TeamAccountsTab />}
            {tab === "billing" && <BillingTab company={company} access={access} reload={load} />}
          </div>
        )}
    </div>
  );
}

function AccessBadge({ access }) {
  const map = {
    lifetime_active: ["Lifetime", "emerald"], annual_active: ["Annual active", "emerald"],
    annual_grace_period: ["Grace period", "amber"], annual_expired: ["Expired", "signal"],
    no_access: ["Free preview", "azure"],
  };
  const [label, color] = map[access.status] || ["—", "faint"];
  return <span className={`hidden sm:inline-flex items-center gap-1.5 rounded-full border border-${color}/30 bg-${color}/10 px-3 py-1 text-[11px] text-${color}`} data-testid="access-badge">{label}</span>;
}

function ExpiryBanner({ access, onRenew }) {
  if (!access) return null;
  if (access.status === "annual_expired") {
    return (
      <div className="mb-6 flex items-center justify-between gap-4 rounded-xl border border-signal/30 bg-signal/10 px-5 py-3.5" data-testid="expiry-banner">
        <div className="flex items-center gap-3"><AlertTriangle size={18} className="text-signal shrink-0" />
          <p className="text-sm text-ink">Your annual access has <span className="text-signal font-medium">expired</span>. Renew or upgrade to lifetime to restore full functionality.</p></div>
        <button onClick={onRenew} className="rounded-full bg-signal px-4 py-2 text-xs font-medium text-white hover:bg-white hover:text-black transition-colors duration-200 whitespace-nowrap">Renew now</button>
      </div>
    );
  }
  if (access.status === "annual_grace_period") {
    return (
      <div className="mb-6 flex items-center justify-between gap-4 rounded-xl border border-amber/30 bg-amber/10 px-5 py-3.5" data-testid="expiry-banner">
        <div className="flex items-center gap-3"><AlertTriangle size={18} className="text-amber shrink-0" />
          <p className="text-sm text-ink">You're in your grace period. Renew now to avoid losing access to search and messaging.</p></div>
        <button onClick={onRenew} className="rounded-full bg-amber px-4 py-2 text-xs font-medium text-black hover:bg-white transition-colors duration-200 whitespace-nowrap">Renew now</button>
      </div>
    );
  }
  if (access.expiring_soon && access.days_until_expiry >= 0) {
    return (
      <div className="mb-6 flex items-center justify-between gap-4 rounded-xl border border-amber/25 bg-amber/5 px-5 py-3.5" data-testid="expiry-banner">
        <div className="flex items-center gap-3"><AlertTriangle size={18} className="text-amber shrink-0" />
          <p className="text-sm text-muted">Your annual access expires in <span className="text-ink font-medium">{access.days_until_expiry} day(s)</span> on {new Date(access.access_expiration).toLocaleDateString("en-US")}.</p></div>
        <button onClick={onRenew} className="rounded-full border border-white/15 px-4 py-2 text-xs text-ink hover:bg-white hover:text-black transition-colors duration-200 whitespace-nowrap">Renew</button>
      </div>
    );
  }
  return null;
}

function TeamTab({ access, nav }) {
  const [description, setDescription] = useState("We are building an AI infrastructure company. We need a founding backend engineer, an ML systems engineer and a product-oriented frontend engineer who can work effectively together.");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const assemble = async () => {
    setBusy(true); setResult(null);
    try { const { data } = await api.post("/team/assemble", { description }); setResult(data); loadSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const [savedTeams, setSavedTeams] = useState([]);
  const loadSaved = () => api.get("/team/list").then((r) => setSavedTeams(r.data.teams)).catch(() => {});
  useEffect(() => { loadSaved(); }, []);
  const loadTeam = async (id) => {
    try { const { data } = await api.get(`/team/${id}`); setResult(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const swap = async (roleIndex, candidateId) => {
    if (!result?.id) return;
    try { const { data } = await api.post(`/team/${result.id}/swap`, { role_index: roleIndex, candidate_id: candidateId }); setResult(data); toast.success("Member swapped"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const exportPdf = async () => {
    if (!result?.id) return;
    try {
      const resp = await api.get(`/team/${result.id}/export`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = "team-brief.pdf"; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed"); }
  };
  const shareTeam = async () => {
    if (!result?.id) return;
    try {
      const { data } = await api.post(`/team/${result.id}/share`);
      const link = `${window.location.origin}/team/shared/${data.token}`;
      try { await navigator.clipboard.writeText(link); } catch (_) {}
      toast.success("Share link copied — valid for 30 days");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const revokeShare = async () => {
    if (!result?.id) return;
    try {
      await api.delete(`/team/${result.id}/share`);
      toast.success("Share link revoked");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Gate access={access}>
      <div className="rounded-2xl border border-white/10 bg-surface p-2 ta-accent-glow mb-6">
        <div className="rounded-xl bg-base/60 p-4">
          <div className="flex items-center gap-2 mb-3"><Users size={14} className="text-azure" /><span className="label-mono text-faint">Describe the whole team you want to build</span></div>
          <textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="team-prompt"
            className="w-full resize-none bg-transparent text-sm text-ink focus:outline-none leading-relaxed" />
          <div className="flex justify-end mt-2">
            <button onClick={assemble} disabled={busy} data-testid="assemble-team-btn"
              className="inline-flex items-center gap-2 rounded-full bg-azure px-5 py-2.5 text-sm font-medium text-white hover:bg-white hover:text-black transition-colors duration-200 disabled:opacity-50">
              {busy ? <><Loader2 size={15} className="animate-spin" /> Assembling…</> : <><Sparkles size={15} /> Assemble a Team</>}
            </button>
          </div>
        </div>
      </div>

      {savedTeams.length > 0 && (
        <div className="mb-6">
          <p className="label-mono text-faint mb-2">Saved teams</p>
          <div className="flex flex-wrap gap-2">
            {savedTeams.map((t) => (
              <button key={t.id} onClick={() => loadTeam(t.id)} data-testid={`saved-team-${t.id}`}
                className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-muted hover:border-azure/40 hover:text-ink transition-colors duration-200">
                {t.team_name}
              </button>
            ))}
          </div>
        </div>
      )}

      {result && (
        <div className="space-y-6" data-testid="team-result">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="label-mono text-azure mb-1">{result.team_name}</p>
              <p className="text-sm text-muted">{result.analysis?.summary}</p>
            </div>
            <button onClick={exportPdf} data-testid="export-team-pdf"
              className="shrink-0 inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs text-ink hover:bg-white hover:text-black transition-colors duration-200">
              <Download size={14} /> Export PDF
            </button>
            <button onClick={shareTeam} data-testid="share-team-btn"
              className="shrink-0 inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs text-ink hover:bg-white hover:text-black transition-colors duration-200">
              <Share2 size={14} /> Share link
            </button>
            <button onClick={revokeShare} data-testid="revoke-team-btn"
              className="shrink-0 inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs text-muted hover:bg-signal hover:text-white transition-colors duration-200">
              Revoke
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {result.roles.map((r, i) => (
              <div key={i} className="rounded-2xl border border-white/10 bg-surface p-5">
                <p className="label-mono text-faint mb-2">{r.role_title}</p>
                {r.member ? (<>
                  <div className="flex items-center justify-between">
                    <p className="font-display font-bold text-ink">{r.member.card.display_name}</p>
                    <span className="font-mono text-azure font-bold">{r.member.score}</span>
                  </div>
                  <p className="text-xs text-muted mt-0.5">{r.member.card.headline}</p>
                  <div className="mt-2 flex flex-wrap gap-1">{(r.member.card.top_skills || []).slice(0, 4).map((s) => <span key={s} className="rounded bg-white/5 px-1.5 py-0.5 text-[10px] font-mono text-muted">{s}</span>)}</div>
                  <button onClick={() => nav(`/employer/candidate/${r.member.card.candidate_id}`)} className="mt-3 text-xs text-azure hover:underline">Open profile →</button>
                  {(r.alternatives || []).length > 0 && (
                    <div className="mt-3 pt-3 border-t border-white/5">
                      <p className="label-mono text-faint mb-1.5">Swap in an alternative</p>
                      <div className="flex flex-wrap gap-1.5">
                        {r.alternatives.map((a) => (
                          <button key={a.card.candidate_id} onClick={() => swap(i, a.card.candidate_id)} data-testid={`swap-${i}-${a.card.candidate_id}`}
                            className="rounded-full border border-white/10 px-2.5 py-1 text-[10px] text-muted hover:border-azure/40 hover:text-ink transition-colors duration-200">
                            {a.card.display_name} ({a.score})
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </>) : <p className="text-sm text-faint">No candidate available for this role yet.</p>}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TeamPanel title="Team strengths" items={result.analysis?.strengths} color="emerald" />
            <TeamPanel title="Missing capabilities" items={result.analysis?.missing_capabilities} color="signal" />
            <TeamPanel title="Working-style risks" items={result.analysis?.working_style_risks} color="amber" />
            <TeamPanel title="Recommended interview sequence" items={result.analysis?.interview_sequence} color="azure" ordered />
          </div>
        </div>
      )}
    </Gate>
  );
}

function TeamPanel({ title, items, color, ordered }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="rounded-2xl border border-white/10 bg-surface p-5">
      <p className={`label-mono text-${color} mb-3`}>{title}</p>
      <ul className="space-y-1.5 text-sm text-muted">
        {items.map((it, i) => <li key={i} className="flex gap-2"><span className="text-faint font-mono text-xs">{ordered ? `${i + 1}.` : "·"}</span> {it}</li>)}
      </ul>
    </div>
  );
}

function RegSection({ title, children }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-surface p-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
      <p className="sm:col-span-2 label-mono text-azure">{title}</p>{children}
    </div>
  );
}

function CompanyRegister({ onDone }) {
  const [f, setF] = useState({ legal_name: "", trading_name: "", website: "", linkedin_company: "", headquarters_country: "United States", headquarters_state: "", headquarters_city: "", year_founded: "", employees: "", industry: "", funding_stage: "", capital_raised: "", investors: "", accelerator: "", yc_batch: "", tech_stack: "", company_description: "", product_description: "", hiring_needs: "", expected_hiring_volume: "", target_roles: "", hiring_timeline: "", international_hiring: "", work_arrangement: [], us_locations: [], relocation_support: false, visa_sponsorship: false, rep_first_name: "", rep_last_name: "", rep_title: "", rep_email: "", rep_phone: "", rep_linkedin: "", founder_status: false, hiring_authority: false, agreements_accepted: false, privacy_accepted: false });
  const [opts, setOpts] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/public/options").then((r) => setOpts(r.data)).catch(() => setOpts({})); }, []);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const input = "w-full rounded-lg border border-white/12 bg-surface px-3.5 py-2.5 text-sm text-ink focus:border-azure focus:outline-none focus:ring-2 focus:ring-azure/40 transition-colors duration-200";
  const label = "label-mono text-faint block mb-1.5";
  const websiteBad = f.website && !isValidUrl(f.website);
  const tf = (k, l, o = {}) => (
    <div className={o.span ? "sm:col-span-2" : ""}>
      <label className={label}>{l}{o.req && <span className="text-signal"> *</span>}</label>
      <input type={o.type || "text"} className={input} value={f[k]} onChange={(e) => set(k, e.target.value)} placeholder={o.ph} data-testid={`company-${k}`} />
      {k === "website" && websiteBad && <p className="mt-1 text-xs text-signal" data-testid="company-website-error">Enter a valid website URL</p>}
    </div>
  );
  const submit = async (e) => {
    e.preventDefault();
    if (!f.legal_name.trim()) return toast.error("Legal company name is required");
    if (!isValidUrl(f.website)) return toast.error("A valid company website is required");
    if (!f.rep_first_name.trim() || !f.rep_last_name.trim() || !f.rep_title.trim()) return toast.error("Your name and title are required");
    if (!f.rep_email.trim()) return toast.error("A work email is required");
    if (!f.funding_stage) return toast.error("Funding stage is required");
    if (!f.agreements_accepted) return toast.error("You must accept the terms");
    setBusy(true);
    try { await api.post("/company/register", { data: f }); toast.success("Application submitted for review"); onDone(); }
    catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); } finally { setBusy(false); }
  };
  if (!opts) return <div className="min-h-screen grid place-items-center text-muted font-mono text-sm">Loading…</div>;
  return (
    <div className="mx-auto max-w-3xl px-5 lg:px-8 py-10">
      <div className="flex items-center gap-2 mb-2"><Building2 className="text-azure" size={18} /><span className="label-mono text-azure">Company application</span></div>
      <h1 className="font-display font-bold text-3xl mb-2">Register your startup</h1>
      <p className="text-muted mb-8">Only approved companies can purchase access and view full candidate profiles. Fields marked <span className="text-signal">*</span> are required.</p>
      <form onSubmit={submit} className="space-y-5" data-testid="company-register-form">
        <RegSection title="Your details (representative)">
          {tf("rep_first_name", "First name", { req: true })}
          {tf("rep_last_name", "Last name", { req: true })}
          {tf("rep_title", "Job title", { req: true, ph: "e.g. Co-founder & CTO" })}
          {tf("rep_email", "Work email", { req: true, ph: "you@yourcompany.com" })}
          {tf("rep_phone", "Phone")}
          {tf("rep_linkedin", "Your LinkedIn")}
          <label className="flex items-center gap-2 text-sm text-muted"><input type="checkbox" className="h-4 w-4 accent-azure" checked={f.founder_status} onChange={(e) => set("founder_status", e.target.checked)} data-testid="company-founder_status" /> I'm a founder</label>
          <label className="flex items-center gap-2 text-sm text-muted"><input type="checkbox" className="h-4 w-4 accent-azure" checked={f.hiring_authority} onChange={(e) => set("hiring_authority", e.target.checked)} /> I have hiring authority</label>
        </RegSection>
        <RegSection title="Company">
          {tf("legal_name", "Legal company name", { req: true })}
          {tf("trading_name", "Trading name")}
          {tf("website", "Website", { req: true, ph: "https://www.company.com" })}
          {tf("linkedin_company", "LinkedIn company page")}
          <div><label className={label}>Country</label><SearchSelect testId="company-country" value={f.headquarters_country} onChange={(v) => set("headquarters_country", v)} options={opts.countries || []} /></div>
          {tf("headquarters_state", "State / region")}
          {tf("headquarters_city", "City")}
          {tf("year_founded", "Year founded", { type: "number" })}
          <div><label className={label}>Employees</label><Select testId="company-employees" value={f.employees} onChange={(v) => set("employees", v)} options={opts.employee_sizes || []} placeholder="Team size" /></div>
          <div><label className={label}>Industry</label><Select testId="company-industry" value={f.industry} onChange={(v) => set("industry", v)} options={opts.industries || []} placeholder="Select industry" /></div>
          <div><label className={label}>Funding stage<span className="text-signal"> *</span></label><Select testId="company-funding_stage" value={f.funding_stage} onChange={(v) => set("funding_stage", v)} options={opts.funding_stages || []} placeholder="Select stage" /></div>
          {tf("capital_raised", "Capital raised", { ph: "e.g. $4.5M" })}
          {tf("investors", "Notable investors")}
          {tf("accelerator", "Accelerator")}
          {tf("yc_batch", "YC batch (optional)")}
          {tf("tech_stack", "Tech stack", { span: true })}
          <div className="sm:col-span-2"><label className={label}>Company description</label><textarea rows={3} className={input} value={f.company_description} onChange={(e) => set("company_description", e.target.value)} /></div>
          <div className="sm:col-span-2"><label className={label}>Product description</label><textarea rows={3} className={input} value={f.product_description} onChange={(e) => set("product_description", e.target.value)} /></div>
        </RegSection>
        <RegSection title="Hiring">
          {tf("target_roles", "Target roles", { span: true, ph: "e.g. Founding backend engineer, ML systems engineer" })}
          <div><label className={label}>Expected hiring volume</label><Select testId="company-expected_hiring_volume" value={f.expected_hiring_volume} onChange={(v) => set("expected_hiring_volume", v)} options={opts.expected_hiring_volume || []} placeholder="How many hires?" /></div>
          <div><label className={label}>Hiring timeline</label><Select testId="company-hiring_timeline" value={f.hiring_timeline} onChange={(v) => set("hiring_timeline", v)} options={opts.hiring_timelines || []} placeholder="When?" /></div>
          <div className="sm:col-span-2"><label className={label}>Work arrangement</label><CheckGroup testId="company-work_arrangement" values={f.work_arrangement} onChange={(v) => set("work_arrangement", v)} options={opts.work_arrangement || []} /></div>
          <div className="sm:col-span-2"><label className={label}>U.S. locations</label><MultiSelect testId="company-us_locations" values={f.us_locations} onChange={(v) => set("us_locations", v)} options={[...(opts.us_hubs || []), ...(opts.us_states || [])]} allowCustom placeholder="Add locations…" /></div>
          <div><label className={label}>International hiring</label><Select testId="company-international_hiring" value={f.international_hiring} onChange={(v) => set("international_hiring", v)} options={opts.international_hiring || []} placeholder="Interest" /></div>
          <div className="flex flex-col gap-2 pt-6">
            <label className="flex items-center gap-2 text-sm text-muted"><input type="checkbox" className="h-4 w-4 accent-azure" checked={f.relocation_support} onChange={(e) => set("relocation_support", e.target.checked)} /> We support relocation</label>
            <label className="flex items-center gap-2 text-sm text-muted"><input type="checkbox" className="h-4 w-4 accent-azure" checked={f.visa_sponsorship} onChange={(e) => set("visa_sponsorship", e.target.checked)} /> We can sponsor visas</label>
          </div>
        </RegSection>
        <label className="flex items-start gap-2.5 text-sm text-muted rounded-lg border border-white/10 p-3"><input type="checkbox" className="h-4 w-4 mt-0.5 accent-azure" checked={f.agreements_accepted} onChange={(e) => set("agreements_accepted", e.target.checked)} data-testid="company-agreements" /> I accept the Terms, candidate confidentiality, non-discrimination commitment, and agree not to redistribute or scrape candidate data. Employment decisions receive human review.<span className="text-signal"> *</span></label>
        <button disabled={busy} data-testid="company-submit" className="rounded-full bg-azure px-6 py-3 text-sm font-medium text-white hover:bg-white hover:text-black transition-colors duration-200 disabled:opacity-50">{busy ? "Submitting…" : "Submit application"}</button>
      </form>
    </div>
  );
}

function PendingApproval({ company }) {
  return (
    <div className="mx-auto max-w-2xl px-5 lg:px-8 py-20 text-center">
      <div className="rounded-2xl border border-white/10 bg-surface p-10">
        <Loader2 className="mx-auto text-azure animate-spin mb-5" size={36} />
        <h1 className="font-display font-bold text-2xl">{company.legal_name}</h1>
        <p className="mt-2 text-muted">Your application is <span className="font-mono text-ink">{company.approval_status}</span>. Team Assembly reviews every company before granting access.</p>
        <p className="mt-4 text-xs text-faint">You'll be notified once approved. Then you can activate access and start searching.</p>
      </div>
    </div>
  );
}

function Gate({ access, children }) {
  if (access?.can_use_paid_features) return children;
  return (
    <div className="rounded-2xl border border-azure/20 bg-azure/5 p-10 text-center" data-testid="upgrade-gate">
      <Lock className="mx-auto text-azure mb-4" size={32} />
      <p className="font-display font-bold text-xl">Unlock this with a subscription</p>
      <p className="mt-2 text-muted text-sm max-w-md mx-auto">Exploring the talent pool and match scores is free. Saved candidates, full profiles, messaging, interviews and mobility open up once you activate a plan.</p>
      <button onClick={() => window.dispatchEvent(new CustomEvent("ta:goto-billing"))} data-testid="gate-view-plans"
        className="mt-5 inline-flex items-center gap-2 rounded-full bg-azure px-6 py-2.5 text-sm font-medium text-white hover:bg-white hover:text-black transition-colors duration-200">
        View plans <ArrowRight size={15} />
      </button>
    </div>
  );
}

function UpsellModal({ onClose, onView }) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 backdrop-blur-sm px-4" data-testid="upsell-modal" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-surface p-7" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-full bg-azure/15"><Lock size={18} className="text-azure" /></div>
          <p className="font-display font-bold text-xl text-ink">Ready to reach this candidate?</p>
        </div>
        <p className="mt-3 text-sm text-muted leading-relaxed">You've found a strong match. Activate a plan to reveal full names, evidence-backed profiles and résumés — and to save, message or interview candidates directly.</p>
        <div className="mt-6 flex gap-2">
          <button onClick={onView} data-testid="upsell-view-plans" className="flex-1 rounded-full bg-azure py-2.5 text-sm font-medium text-white hover:bg-white hover:text-black transition-colors duration-200">View plans</button>
          <button onClick={onClose} data-testid="upsell-dismiss" className="rounded-full border border-white/15 px-5 py-2.5 text-sm text-ink hover:bg-white/5 transition-colors duration-200">Maybe later</button>
        </div>
      </div>
    </div>
  );
}

function SearchTab({ access, nav, setTab }) {
  const [prompt, setPrompt] = useState(localStorage.getItem("ta_prefill_prompt") || "We need a founding backend engineer with strong Python, Go, distributed-systems and PostgreSQL experience, comfortable working with founders and willing to relocate to New York.");
  const [criteria, setCriteria] = useState(null);
  const [results, setResults] = useState([]);
  const [noMatch, setNoMatch] = useState(null);
  const [adjacent, setAdjacent] = useState([]);
  const [phase, setPhase] = useState("idle");
  const [preview, setPreview] = useState(null);
  const [minScore, setMinScore] = useState(0);
  const [savedIds, setSavedIds] = useState(new Set());
  const [tplSaved, setTplSaved] = useState(false);
  const [showUpsell, setShowUpsell] = useState(false);
  const previewMode = !access?.can_use_paid_features;

  useEffect(() => {
    if (phase !== "review" || !criteria) { setPreview(null); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.post("/search/preview", { criteria, min_score: minScore }); setPreview(data); }
      catch { setPreview(null); }
    }, 350);
    return () => clearTimeout(t);
  }, [criteria, phase, minScore]);

  useEffect(() => {
    const raw = localStorage.getItem("ta_prefill_criteria");
    if (raw) {
      try { setCriteria(JSON.parse(raw)); setPhase("review"); } catch { /* ignore */ }
      localStorage.removeItem("ta_prefill_criteria");
    }
  }, []);

  const interpret = async () => {
    setPhase("interpreting"); setResults([]); setNoMatch(null); setAdjacent([]); setTplSaved(false);
    try {
      const { data } = await api.post("/search/interpret", { prompt });
      const c = data.criteria || {};
      const onsite = /onsite/i.test(c.remote_policy || c.work_arrangement || "");
      setCriteria({
        min_years_mandatory: true,
        seniority_mandatory: false,
        ...c,
        location_mandatory: c.location_mandatory ?? (!!c.relocation_required || onsite),
      });
      setPhase("review");
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setPhase("idle"); }
  };
  const run = async (includeAdjacent = false) => {
    setPhase("searching");
    try {
      const { data } = await api.post("/search/run", { criteria, include_adjacent: includeAdjacent, min_score: minScore });
      setResults(data.results); setNoMatch(data.no_match || null); setAdjacent(data.adjacent || []);
      setPhase("results");
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setPhase("review"); }
  };
  const save = async (id) => {
    if (previewMode) { setShowUpsell(true); return; }
    try { await api.post("/search/save", { candidate_id: id }); setSavedIds((p) => new Set(p).add(id)); toast.success("Saved candidate"); }
    catch { toast.error("Failed"); }
  };
  const saveTemplate = async () => {
    const title = window.prompt("Name this role template (e.g. 'Founding Backend Engineer')");
    if (!title || !title.trim()) return;
    try { await api.post("/search/roles", { title: title.trim(), criteria }); setTplSaved(true); toast.success("Template saved to Roles"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <>
      {previewMode && (
        <div className="mb-6 flex items-center justify-between gap-4 rounded-xl border border-azure/30 bg-azure/10 px-5 py-3.5" data-testid="preview-banner">
          <div className="flex items-center gap-3"><Sparkles size={18} className="text-azure shrink-0" />
            <p className="text-sm text-ink">You're exploring in <span className="text-azure font-medium">free preview</span>. Search the full talent pool and see match scores — candidate identities and full profiles unlock with a subscription.</p></div>
          <button onClick={() => setTab && setTab("billing")} data-testid="preview-view-plans"
            className="rounded-full bg-azure px-4 py-2 text-xs font-medium text-white hover:bg-white hover:text-black transition-colors duration-200 whitespace-nowrap">View plans</button>
        </div>
      )}
      <div className="rounded-2xl border border-white/10 bg-surface p-2 ta-accent-glow mb-6">
        <div className="rounded-xl bg-base/60 p-4">
          <div className="flex items-center gap-2 mb-3"><Search size={14} className="text-azure" /><span className="label-mono text-faint">Describe the role</span></div>
          <textarea rows={3} value={prompt} onChange={(e) => setPrompt(e.target.value)} data-testid="search-prompt"
            className="w-full resize-none bg-transparent text-sm text-ink focus:outline-none leading-relaxed" />
          <div className="flex justify-end mt-2">
            <button onClick={interpret} disabled={phase === "interpreting"} data-testid="interpret-btn"
              className="inline-flex items-center gap-2 rounded-full bg-azure px-5 py-2.5 text-sm font-medium text-white hover:bg-white hover:text-black transition-colors duration-200 disabled:opacity-50">
              {phase === "interpreting" ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Interpret with AI
            </button>
          </div>
        </div>
      </div>

      {criteria && (phase === "review" || phase === "searching") && (
        <div className="rounded-2xl border border-white/10 bg-surface p-6 mb-6" data-testid="criteria-panel">
          <div className="flex items-center justify-between mb-4">
            <span className="label-mono text-azure">Interpreted criteria — edit before searching</span>
            {criteria._needs_review && <span className="text-[11px] text-amber">Please review — AI interpretation used a fallback</span>}
          </div>
          <SkillManager
            required={criteria.required_skills || []}
            preferred={criteria.preferred_skills || []}
            onChange={(req, pref) => setCriteria({ ...criteria, required_skills: req, preferred_skills: pref })}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm mt-4">
            <EditField label="Role" v={criteria.role_title} on={(x) => setCriteria({ ...criteria, role_title: x })} />
            <EditField label="Startup stage" v={criteria.startup_stage} on={(x) => setCriteria({ ...criteria, startup_stage: x })} />
            <EditField label="Min years" v={criteria.min_years} on={(x) => setCriteria({ ...criteria, min_years: Number(x) || 0 })} />
            <EditField label="Seniority" v={criteria.seniority} on={(x) => setCriteria({ ...criteria, seniority: x })} />
            <EditField label="Location" v={criteria.location} on={(x) => setCriteria({ ...criteria, location: x })} />
            <EditField label="Remote policy" v={criteria.remote_policy} on={(x) => setCriteria({ ...criteria, remote_policy: x })} />
          </div>
          <div className="mt-4 space-y-2" data-testid="mandatory-toggles">
            <p className="label-mono text-faint">Mark each requirement mandatory or preferred</p>
            {criteria.min_years > 0 && (
              <MandToggle label={`Minimum ${criteria.min_years}+ years of experience`} testid="toggle-years"
                on={criteria.min_years_mandatory !== false} set={(v) => setCriteria({ ...criteria, min_years_mandatory: v })} />
            )}
            {criteria.seniority && (
              <MandToggle label={`Seniority: ${criteria.seniority}`} testid="toggle-seniority"
                on={!!criteria.seniority_mandatory} set={(v) => setCriteria({ ...criteria, seniority_mandatory: v })} />
            )}
            {(criteria.location || /onsite/i.test(criteria.remote_policy || "") || criteria.relocation_required) && (
              <MandToggle label={`Location / onsite${criteria.location ? " — " + criteria.location : ""}`} testid="toggle-location"
                on={!!criteria.location_mandatory} set={(v) => setCriteria({ ...criteria, location_mandatory: v })} />
            )}
          </div>
          <label className="flex items-center gap-2 mt-3 text-sm text-muted">
            <input type="checkbox" className="h-4 w-4 accent-azure" checked={!!criteria.relocation_required} onChange={(e) => setCriteria({ ...criteria, relocation_required: e.target.checked })} /> Candidate must be open to relocation
          </label>
          <div className="mt-4 rounded-lg border border-white/10 bg-base/40 px-4 py-3" data-testid="relevance-threshold">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-ink">Hide candidates below {minScore}% relevance</span>
              <span className="label-mono text-faint">{minScore === 0 ? "showing all" : `≥ ${minScore}`}</span>
            </div>
            <input type="range" min="0" max="90" step="5" value={minScore}
              onChange={(e) => setMinScore(Number(e.target.value))} data-testid="relevance-slider"
              className="w-full accent-azure cursor-pointer" />
            <p className="mt-1 text-[11px] text-faint">On-domain matches usually score 70+. Raise this to keep the list tight — off-domain profiles drop out.</p>
          </div>
          <div className="mt-5 flex flex-wrap items-center gap-2">
            <button onClick={() => run(false)} disabled={phase === "searching"} data-testid="run-search-btn"
              className="inline-flex items-center gap-2 rounded-full bg-emerald px-6 py-3 text-sm font-medium text-black hover:bg-white transition-colors duration-200 disabled:opacity-50">
              {phase === "searching" ? <><Loader2 size={15} className="animate-spin" /> Searching evidence…</> : <>Run search <ArrowRight size={15} /></>}
            </button>
            <button onClick={saveTemplate} data-testid="save-template-btn"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-3 text-sm text-ink hover:bg-white/5 transition-colors duration-200">
              <Bookmark size={15} fill={tplSaved ? "currentColor" : "none"} className={tplSaved ? "text-white" : ""} /> {tplSaved ? "Saved to Roles" : "Save as template"}
            </button>
            {preview && (
              <span data-testid="live-match-count"
                className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm ${preview.eligible > 0 ? "border-emerald/30 bg-emerald/10 text-emerald" : "border-signal/30 bg-signal/10 text-signal"}`}>
                <span className="font-display font-bold">{preview.eligible}</span>
                <span className="text-xs opacity-80">of {preview.total} candidates would match</span>
              </span>
            )}
          </div>
        </div>
      )}

      {phase === "results" && (
        <div className="space-y-4" data-testid="results-list">
          {results.length === 0 ? (
            <div className="rounded-2xl border border-amber/25 bg-amber/5 p-8" data-testid="results-empty">
              <div className="flex items-start gap-3">
                <SearchX className="text-amber shrink-0 mt-0.5" size={24} />
                <div>
                  <p className="font-display font-bold text-lg text-ink">No approved candidates currently match all mandatory requirements in your search.</p>
                  <p className="mt-1 text-sm text-muted">Team Assembly only returns candidates with verified evidence for every mandatory requirement. Nothing went wrong — no vetted candidate satisfies all of them right now.</p>
                </div>
              </div>
              {noMatch?.unmet_requirements?.length > 0 && (
                <div className="mt-4 rounded-xl border border-white/10 bg-base/50 p-4" data-testid="unmet-requirements">
                  <p className="label-mono text-faint mb-2">Unmet requirements</p>
                  <ul className="space-y-1.5">
                    {noMatch.unmet_requirements.map((u, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-ink"><span className="text-signal mt-0.5">✕</span> {u}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="mt-5 flex flex-wrap gap-2">
                <button onClick={() => setPhase("review")} data-testid="refine-search-btn"
                  className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm text-ink hover:bg-white/5 transition-colors duration-200">
                  <ArrowLeft size={15} /> Refine criteria
                </button>
                {adjacent.length === 0 && (
                  <button onClick={() => run(true)} data-testid="show-adjacent-btn"
                    className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm text-muted hover:text-ink hover:bg-white/5 transition-colors duration-200">
                    Show adjacent profiles
                  </button>
                )}
              </div>
            </div>
          ) : (
          <p className="label-mono text-faint">{results.length} evidence-backed matches — every mandatory requirement verified</p>
          )}
          {results.map((r, idx) => (
            <div key={r.candidate_id} className="rounded-2xl border border-white/10 bg-surface p-6 hover:border-azure/40 transition-colors duration-200">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-3">
                    <p className="font-display font-bold text-lg text-ink flex items-center gap-2">{r.card.locked && <Lock size={15} className="text-faint" />}{r.card.locked ? `Candidate #${idx + 1}` : r.card.display_name}</p>
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald/10 px-2 py-0.5 text-[10px] text-emerald"><BadgeCheck size={10} /> {(r.card.verification_level || "").replace(/_/g, " ")}</span>
                    {r.card.open_to_offers && <span className="inline-flex items-center gap-1 rounded-full bg-azure/10 px-2 py-0.5 text-[10px] text-azure">Open to offers</span>}
                  </div>
                  <p className="text-sm text-muted mt-0.5">{r.card.headline}</p>
                  <p className="text-xs text-faint font-mono mt-1">{r.card.country} · {r.card.years_experience} yrs · {r.card.project_count} projects</p>
                </div>
                <div className="text-right">
                  <p className="font-display font-bold text-2xl text-azure">{r.score}</p>
                  <p className="label-mono text-faint">match</p>
                </div>
              </div>
              <p className="mt-3 text-sm text-ink/90 leading-relaxed">{r.explanation}</p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {r.factors.slice(0, 4).map((f, i) => <span key={i} className="rounded bg-azure/10 px-2 py-0.5 text-[10px] text-azure">{f.label}</span>)}
                {r.gaps.slice(0, 2).map((g, i) => <span key={i} className="rounded bg-signal/10 px-2 py-0.5 text-[10px] text-signal">Gap: {g}</span>)}
              </div>
              <div className="mt-4 flex gap-2">
                {r.card.locked ? (
                  <button onClick={() => setShowUpsell(true)} data-testid={`unlock-${r.candidate_id}`}
                    className="inline-flex items-center gap-1.5 rounded-full bg-azure px-4 py-2 text-xs font-medium text-white hover:bg-white hover:text-black transition-colors duration-200"><Lock size={12} /> Unlock full profile</button>
                ) : (
                  <>
                    <button onClick={() => nav(`/employer/candidate/${r.candidate_id}`)} data-testid={`view-${r.candidate_id}`} className="rounded-full bg-azure px-4 py-2 text-xs font-medium text-white hover:bg-white hover:text-black transition-colors duration-200">Open profile</button>
                    <button onClick={() => save(r.candidate_id)} data-testid={`save-${r.candidate_id}`}
                      className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-2 text-xs text-ink hover:bg-white/5 transition-colors duration-200">
                      <Bookmark size={13} fill={savedIds.has(r.candidate_id) ? "currentColor" : "none"} className={savedIds.has(r.candidate_id) ? "text-white" : ""} /> {savedIds.has(r.candidate_id) ? "Saved" : "Save"}
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}

          {adjacent.length > 0 && (
            <div className="mt-8 pt-6 border-t border-white/10" data-testid="adjacent-section">
              <p className="font-display font-bold text-base text-ink">Adjacent profiles — missing one or more mandatory requirements</p>
              <p className="text-sm text-muted mt-1 mb-4">These are <span className="text-signal">not matches</span>. Each is missing at least one requirement you marked mandatory.</p>
              <div className="space-y-3">
                {adjacent.map((r) => (
                  <div key={r.candidate_id} className="rounded-2xl border border-signal/20 bg-signal/5 p-5" data-testid={`adjacent-${r.candidate_id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-display font-bold text-ink flex items-center gap-2">{r.card.locked && <Lock size={13} className="text-faint" />}{r.card.display_name}</p>
                        <p className="text-sm text-muted">{r.card.headline}</p>
                      </div>
                      <button onClick={() => r.card.locked ? setShowUpsell(true) : nav(`/employer/candidate/${r.candidate_id}`)} className="rounded-full border border-white/15 px-4 py-2 text-xs text-ink hover:bg-white/5 transition-colors duration-200">{r.card.locked ? "Unlock profile" : "Open profile"}</button>
                    </div>
                    <div className="mt-3 space-y-1">
                      {r.missing.map((m, i) => (
                        <p key={i} className="text-xs text-signal">Missing mandatory requirement: {m.reason.replace(/^No evidence of required skill: /, "").trim()}{m.reason.startsWith("No evidence") ? " (skill)" : ""}</p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      {showUpsell && <UpsellModal onClose={() => setShowUpsell(false)} onView={() => { setShowUpsell(false); setTab && setTab("billing"); }} />}
    </>
  );
}

function TeamAccountsTab() {
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/company/team").then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);
  if (!data) return <p className="text-muted text-sm">Loading team…</p>;
  const isOwner = data.is_owner;
  const full = data.seats.remaining <= 0;

  const invite = async () => {
    if (!email.trim()) return;
    setBusy(true);
    try { await api.post("/company/team/invite", { email: email.trim() }); toast.success("Invite sent"); setEmail(""); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const revoke = async (id) => { try { await api.post(`/company/team/invite/${id}/revoke`); toast.success("Invite revoked"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const remove = async (uid) => { try { await api.post(`/company/team/member/${uid}/remove`); toast.success("Member removed"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  return (
    <div className="space-y-6" data-testid="team-accounts">
      <div className="rounded-2xl border border-white/10 bg-surface p-6">
        <div className="flex items-center justify-between mb-1">
          <p className="font-display font-bold text-lg text-ink">Your team</p>
          <span className="label-mono text-faint" data-testid="team-seats">{data.seats.used} / {data.seats.max} seats</span>
        </div>
        <p className="text-sm text-muted mb-5">Everyone on your team shares the same searches, saved candidates and conversations.</p>

        {isOwner && (
          <div className="flex flex-col sm:flex-row gap-2 mb-6">
            <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="teammate@yourstartup.com" data-testid="invite-email-input"
              className="flex-1 rounded-full border border-white/12 bg-base px-4 py-2.5 text-sm text-ink focus:border-azure focus:outline-none transition-colors duration-200" />
            <button onClick={invite} disabled={busy || full} data-testid="invite-btn"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-azure px-5 py-2.5 text-sm font-medium text-white hover:bg-white hover:text-black transition-colors duration-200 disabled:opacity-50">
              <UserPlus size={15} /> {full ? "Team full" : "Send invite"}
            </button>
          </div>
        )}

        <div className="space-y-2" data-testid="team-members">
          {data.members.map((m) => (
            <div key={m.user_id} className="flex items-center justify-between rounded-xl border border-white/10 bg-base/50 px-4 py-3">
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-ink">{m.name}{m.is_you && <span className="text-faint"> (you)</span>}</p>
                  {m.company_role === "owner"
                    ? <span className="inline-flex items-center gap-1 rounded-full bg-amber/10 px-2 py-0.5 text-[10px] text-amber"><Crown size={10} /> Owner</span>
                    : <span className="rounded-full bg-azure/10 px-2 py-0.5 text-[10px] text-azure">Member</span>}
                </div>
                <p className="text-xs text-faint font-mono mt-0.5">{m.email}</p>
              </div>
              {isOwner && m.company_role !== "owner" && (
                <button onClick={() => remove(m.user_id)} data-testid={`remove-member-${m.user_id}`}
                  className="text-muted hover:text-signal transition-colors duration-200"><Trash2 size={16} /></button>
              )}
            </div>
          ))}
        </div>

        {data.invites.length > 0 && (
          <div className="mt-6">
            <p className="label-mono text-faint mb-2">Pending invites</p>
            <div className="space-y-2" data-testid="team-invites">
              {data.invites.map((i) => (
                <div key={i.id} className="flex items-center justify-between rounded-xl border border-white/10 bg-base/30 px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Mail size={14} className="text-faint" />
                    <p className="text-sm text-ink">{i.email}</p>
                    <span className="rounded-full bg-amber/10 px-2 py-0.5 text-[10px] text-amber">Pending</span>
                  </div>
                  {isOwner && (
                    <button onClick={() => revoke(i.id)} data-testid={`revoke-invite-${i.id}`}
                      className="text-xs text-muted hover:text-signal transition-colors duration-200">Revoke</button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {!isOwner && <p className="mt-5 text-xs text-faint">Only the workspace owner can invite or remove teammates.</p>}
      </div>
    </div>
  );
}

function SkillManager({ required, preferred, onChange }) {
  const [input, setInput] = useState("");
  const add = () => {
    const v = input.trim();
    if (!v) return;
    if (![...required, ...preferred].some((s) => s.toLowerCase() === v.toLowerCase()))
      onChange([...required, v], preferred);
    setInput("");
  };
  const toPreferred = (s) => onChange(required.filter((x) => x !== s), [...preferred, s]);
  const toRequired = (s) => onChange([...required, s], preferred.filter((x) => x !== s));
  const removeReq = (s) => onChange(required.filter((x) => x !== s), preferred);
  const removePref = (s) => onChange(required, preferred.filter((x) => x !== s));
  return (
    <div data-testid="skill-manager">
      <p className="label-mono text-faint mb-2">Mandatory skills — candidates must have verified evidence for all</p>
      <div className="flex flex-wrap gap-1.5 mb-3" data-testid="mandatory-skills">
        {required.length === 0 && <span className="text-xs text-faint">None — add a skill below</span>}
        {required.map((s) => (
          <span key={s} className="inline-flex items-center gap-1.5 rounded-full bg-azure/15 border border-azure/30 px-2.5 py-1 text-xs text-azure" data-testid={`mand-skill-${s}`}>
            {s}
            <button title="Make preferred" onClick={() => toPreferred(s)} className="text-azure/70 hover:text-white">→</button>
            <button title="Remove" onClick={() => removeReq(s)} className="text-azure/70 hover:text-signal">×</button>
          </span>
        ))}
      </div>
      <p className="label-mono text-faint mb-2">Preferred skills — nice to have, never exclude a candidate</p>
      <div className="flex flex-wrap gap-1.5 mb-3" data-testid="preferred-skills">
        {preferred.length === 0 && <span className="text-xs text-faint">None</span>}
        {preferred.map((s) => (
          <span key={s} className="inline-flex items-center gap-1.5 rounded-full bg-white/5 border border-white/12 px-2.5 py-1 text-xs text-muted" data-testid={`pref-skill-${s}`}>
            <button title="Make mandatory" onClick={() => toRequired(s)} className="text-faint hover:text-azure">←</button>
            {s}
            <button title="Remove" onClick={() => removePref(s)} className="text-faint hover:text-signal">×</button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder="Add a skill…" data-testid="add-skill-input"
          className="flex-1 rounded-lg border border-white/12 bg-base px-3 py-2 text-sm text-ink focus:border-azure focus:outline-none transition-colors duration-200" />
        <button onClick={add} data-testid="add-skill-btn" className="rounded-lg border border-white/15 px-4 py-2 text-xs text-ink hover:bg-white/5 transition-colors duration-200">Add mandatory</button>
      </div>
    </div>
  );
}

function MandToggle({ label, on, set, testid }) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-white/10 bg-base/40 px-3 py-2">
      <span className="text-sm text-ink">{label}</span>
      <div className="flex rounded-full border border-white/12 overflow-hidden text-[11px]" data-testid={testid}>
        <button onClick={() => set(true)} data-testid={`${testid}-mandatory`}
          className={`px-3 py-1 transition-colors duration-200 ${on ? "bg-azure text-white" : "text-muted hover:text-ink"}`}>Mandatory</button>
        <button onClick={() => set(false)} data-testid={`${testid}-preferred`}
          className={`px-3 py-1 transition-colors duration-200 ${!on ? "bg-white/10 text-ink" : "text-muted hover:text-ink"}`}>Preferred</button>
      </div>
    </div>
  );
}

function EditField({ label, v, on }) {
  return (
    <div><label className="label-mono text-faint block mb-1">{label}</label>
      <input value={v ?? ""} onChange={(e) => on(e.target.value)}
        className="w-full rounded-lg border border-white/12 bg-base px-3 py-2 text-sm text-ink focus:border-azure focus:outline-none transition-colors duration-200" /></div>
  );
}

function SavedTab({ nav, access }) {
  const [saved, setSaved] = useState([]);
  useEffect(() => { api.get("/search/saved").then((r) => setSaved(r.data.saved)).catch(() => {}); }, []);
  return (
    <Gate access={access}>
      {saved.length === 0 ? <p className="text-muted text-sm">No saved candidates yet.</p> : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="saved-list">
          {saved.map((c) => (
            <div key={c.candidate_id} className="rounded-xl border border-white/10 bg-surface p-5">
              <p className="font-display font-bold text-ink">{c.display_name}</p>
              <p className="text-sm text-muted">{c.headline}</p>
              <button onClick={() => nav(`/employer/candidate/${c.candidate_id}`)} className="mt-3 text-xs text-azure hover:underline">Open profile →</button>
            </div>
          ))}
        </div>
      )}
    </Gate>
  );
}

function RolesTab({ access, setTab }) {
  const [roles, setRoles] = useState([]);
  const load = () => api.get("/search/roles").then((r) => setRoles(r.data.roles)).catch(() => {});
  useEffect(() => { load(); }, []);
  const applyTemplate = (r) => {
    localStorage.setItem("ta_prefill_criteria", JSON.stringify(r.criteria || {}));
    toast.success(`Loaded "${r.title}" into search`);
    setTab && setTab("search");
  };
  const del = async (id) => {
    try { await api.delete(`/search/roles/${id}`); toast.success("Template deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <>
      {roles.length === 0 ? <p className="text-muted text-sm">No role templates yet. Tune the mandatory/preferred criteria on the Search tab, then click <span className="text-ink">Save as template</span>.</p> : (
        <div className="space-y-3" data-testid="roles-list">{roles.map((r) => (
          <div key={r.id} className="rounded-xl border border-white/10 bg-surface p-5" data-testid={`role-${r.id}`}>
            <div className="flex justify-between items-start gap-3">
              <div>
                <p className="font-display font-bold text-ink">{r.title}</p>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {(r.criteria?.required_skills || []).map((s) => (
                    <span key={s} className="rounded-full bg-azure/15 border border-azure/30 px-2 py-0.5 text-[10px] text-azure">{s}</span>
                  ))}
                  {(r.criteria?.preferred_skills || []).map((s) => (
                    <span key={s} className="rounded-full bg-white/5 border border-white/12 px-2 py-0.5 text-[10px] text-muted">{s}</span>
                  ))}
                  {r.criteria?.min_years ? <span className="rounded-full bg-white/5 border border-white/12 px-2 py-0.5 text-[10px] text-muted">{r.criteria.min_years}+ yrs</span> : null}
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => applyTemplate(r)} data-testid={`use-role-${r.id}`}
                  className="rounded-full bg-azure px-4 py-2 text-xs font-medium text-white hover:bg-white hover:text-black transition-colors duration-200">Use template</button>
                <button onClick={() => del(r.id)} data-testid={`delete-role-${r.id}`}
                  className="rounded-full border border-white/15 px-3 py-2 text-xs text-muted hover:text-signal transition-colors duration-200"><Trash2 size={14} /></button>
              </div>
            </div>
          </div>
        ))}</div>
      )}
    </>
  );
}

function MessagesTab() {
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [body, setBody] = useState("");
  useEffect(() => { api.get("/messages/threads").then((r) => setThreads(r.data.threads)).catch(() => {}); }, []);
  const open = async (t) => { setActive(t); const { data } = await api.get(`/messages/thread/${t.thread_id}`); setMsgs(data.messages); };
  const send = async () => { if (!body.trim()) return; await api.post("/messages/reply", { thread_id: active.thread_id, body }); setBody(""); open(active); };
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div className="rounded-xl border border-white/10 bg-surface p-3 space-y-1">
        {threads.length === 0 && <p className="text-sm text-muted p-3">No conversations yet.</p>}
        {threads.map((t) => (
          <button key={t.thread_id} onClick={() => open(t)} className={`w-full text-left rounded-lg p-3 transition-colors duration-200 ${active?.thread_id === t.thread_id ? "bg-azure/10" : "hover:bg-white/5"}`}>
            <p className="text-sm text-ink font-medium">{t.candidate_name}</p>
            <p className="text-xs text-faint truncate">{t.last_body}</p>
          </button>
        ))}
      </div>
      <div className="md:col-span-2 rounded-xl border border-white/10 bg-surface p-4 flex flex-col min-h-[400px]">
        {!active ? <p className="text-sm text-muted m-auto">Select a conversation.</p> : (<>
          <div className="flex-1 space-y-2 overflow-y-auto">
            {msgs.map((m) => (
              <div key={m.id} className={`max-w-[75%] rounded-xl px-3 py-2 text-sm ${m.sender === "company" ? "ml-auto bg-azure/15 text-ink" : "bg-white/5 text-muted"}`}>{m.body}</div>
            ))}
          </div>
          <div className="mt-3 flex gap-2">
            <input value={body} onChange={(e) => setBody(e.target.value)} placeholder="Message…" className="flex-1 rounded-lg border border-white/12 bg-base px-3 py-2 text-sm text-ink focus:border-azure focus:outline-none" />
            <button onClick={send} className="rounded-full bg-azure px-4 py-2 text-sm text-white hover:bg-white hover:text-black transition-colors duration-200">Send</button>
          </div>
        </>)}
      </div>
    </div>
  );
}

function InterviewsTab({ access }) {
  const [reqs, setReqs] = useState([]);
  const load = () => api.get("/messages/interview-requests").then((r) => setReqs(r.data.requests)).catch(() => {});
  useEffect(() => { load(); }, []);
  const download = async (id) => {
    try {
      const resp = await api.get(`/messages/interview-requests/${id}/invite.ics`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "text/calendar" }));
      const a = document.createElement("a"); a.href = url; a.download = "interview.ics"; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); }
  };
  return (
    <Gate access={access}>
      {reqs.length === 0 ? <p className="text-muted text-sm">No interview requests yet. Request one from a candidate's profile.</p> : (
        <div className="space-y-3" data-testid="interviews-list">
          {reqs.map((r) => <EmployerInterviewCard key={r.id} r={r} onDone={load} onDownload={() => download(r.id)} />)}
        </div>
      )}
    </Gate>
  );
}

function EmployerInterviewCard({ r, onDone, onDownload }) {
  const options = (r.candidate_proposed_times || "").split(/[,\n]/).map((s) => s.trim()).filter(Boolean);
  const [chosen, setChosen] = useState(options[0] || "");
  const confirm = async () => {
    try { await api.post(`/messages/interview-requests/${r.id}/confirm`, { chosen_time: chosen }); toast.success("Interview scheduled — invite ready"); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="rounded-xl border border-white/10 bg-surface p-5" data-testid={`emp-interview-${r.id}`}>
      <div className="flex items-center justify-between gap-3">
        <p className="font-display font-bold text-ink">{r.candidate_name}</p>
        <span className={`text-[11px] font-mono ${r.status === "scheduled" ? "text-emerald" : r.status === "accepted" ? "text-azure" : r.status === "declined" ? "text-signal" : "text-amber"}`}>{(r.status || "").replace(/_/g, " ")}</span>
      </div>
      <p className="text-sm text-muted mt-1">{r.message}</p>
      {r.status === "accepted" && options.length > 0 && (
        <div className="mt-3">
          <p className="label-mono text-faint mb-1.5">Candidate proposed times — pick one</p>
          <div className="flex flex-wrap gap-2 items-center">
            <select value={chosen} onChange={(e) => setChosen(e.target.value)} data-testid={`time-select-${r.id}`}
              className="rounded-lg border border-white/12 bg-base px-3 py-2 text-sm text-ink focus:border-azure focus:outline-none">
              {options.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
            <button onClick={confirm} data-testid={`confirm-time-${r.id}`} className="rounded-full bg-azure px-4 py-2 text-xs font-medium text-white hover:bg-white hover:text-black transition-colors duration-200">Confirm & send invite</button>
          </div>
        </div>
      )}
      {r.status === "accepted" && options.length === 0 && (
        <p className="mt-2 text-xs text-faint">Candidate accepted; awaiting proposed times.</p>
      )}
      {r.status === "scheduled" && (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <p className="text-sm text-emerald font-mono">Scheduled: {r.scheduled_time}</p>
          <button onClick={onDownload} data-testid={`ics-${r.id}`} className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs text-ink hover:bg-white hover:text-black transition-colors duration-200"><CalendarClock size={13} /> Download calendar invite</button>
        </div>
      )}
    </div>
  );
}

function MobilityTab({ access }) {
  const [cases, setCases] = useState([]);
  const [f, setF] = useState({ proposed_location: "", proposed_role: "", start_date: "", duration: "", compensation: "", notes: "" });
  const load = () => api.get("/mobility/mine").then((r) => setCases(r.data.cases)).catch(() => {});
  useEffect(() => { load(); }, []);
  const submit = async (e) => { e.preventDefault(); try { await api.post("/mobility/inquiry", f); toast.success("Mobility inquiry submitted"); load(); } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); } };
  const input = "w-full rounded-lg border border-white/12 bg-surface px-3 py-2 text-sm text-ink focus:border-azure focus:outline-none";
  return (
    <Gate access={access}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <form onSubmit={submit} className="rounded-2xl border border-white/10 bg-surface p-6 space-y-3" data-testid="mobility-form">
          <p className="font-display font-bold text-lg">Global Talent Mobility inquiry</p>
          {[["proposed_location", "Proposed U.S. location"], ["proposed_role", "Proposed role"], ["start_date", "Start date"], ["duration", "Duration"], ["compensation", "Compensation"]].map(([k, l]) => (
            <input key={k} className={input} placeholder={l} required={k === "proposed_location" || k === "proposed_role"} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`mobility-${k}`} />
          ))}
          <textarea className={input} rows={2} placeholder="Notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
          <button className="rounded-full bg-azure px-5 py-2.5 text-sm font-medium text-white hover:bg-white hover:text-black transition-colors duration-200" data-testid="mobility-submit">Submit inquiry</button>
          <p className="text-[11px] text-faint">Team Assembly is not a law firm. No visa outcome can be guaranteed.</p>
        </form>
        <div className="space-y-3">
          <p className="label-mono text-faint">Your mobility cases</p>
          {cases.length === 0 ? <p className="text-sm text-muted">No inquiries yet.</p> : cases.map((c) => (
            <div key={c.id} className="rounded-xl border border-white/10 bg-surface p-4">
              <p className="text-sm text-ink font-medium">{c.proposed_role} — {c.proposed_location}</p>
              <p className="text-xs text-faint font-mono mt-1">{(c.status || "").replace(/_/g, " ")}</p>
            </div>
          ))}
        </div>
      </div>
    </Gate>
  );
}

function BillingTab({ company, access, reload }) {
  const [pricing, setPricing] = useState(null);
  const [busy, setBusy] = useState("");
  useEffect(() => { api.get("/public/pricing").then((r) => setPricing(r.data)).catch(() => {}); }, []);
  const checkout = async (plan) => {
    setBusy(plan);
    try { const { data } = await api.post("/payments/checkout", { plan, origin_url: window.location.origin }); window.location.href = data.checkout_url; }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(""); }
  };
  const openPortal = async () => {
    try { const { data } = await api.post("/payments/portal", { origin_url: window.location.origin }); window.location.href = data.url; }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="max-w-3xl">
      <div className="rounded-2xl border border-white/10 bg-surface p-6 mb-6">
        <p className="label-mono text-faint mb-2">Current access</p>
        <p className="font-display font-bold text-2xl text-ink">{(access?.status || "no access").replace(/_/g, " ")}</p>
        {access?.access_expiration && <p className="text-sm text-muted mt-1">Expires {new Date(access.access_expiration).toLocaleDateString("en-US")}</p>}
        {access?.is_lifetime && <p className="text-sm text-emerald mt-1">Lifetime access — no annual expiration.</p>}
      </div>

      {pricing?.plans && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {pricing.plans.map((p) => {
            const lifetimeOwned = !!access?.is_lifetime;
            const annualActive = access?.active && access?.plan === "annual";
            const isCurrent = access?.active && access?.plan === p.key;
            const upgradeAvailable = annualActive && pricing?.upgrade?.active;
            let label = p.button_text;
            let disabled = busy === p.key;
            if (lifetimeOwned) { label = isCurrent ? "Current plan" : "Included in lifetime"; disabled = true; }
            else if (p.key === "annual" && annualActive) {
              if (access?.expiring_soon) { label = "Renew annual"; }
              else { label = "Current plan"; disabled = true; }
            } else if (p.key === "lifetime" && upgradeAvailable) {
              label = "Upgrade below ↓"; disabled = true;
            }
            return (
              <div key={p.key} data-testid={`plan-card-${p.key}`}
                className={`relative rounded-2xl border bg-surface p-6 ${isCurrent ? "border-emerald/60" : p.recommended ? "border-azure/50" : "border-white/10"}`}>
                {isCurrent && (
                  <span className="absolute top-4 right-4 inline-flex items-center gap-1 rounded-full bg-emerald/15 px-2.5 py-0.5 text-[11px] font-medium text-emerald" data-testid={`current-plan-${p.key}`}>
                    <BadgeCheck size={12} /> Current plan
                  </span>
                )}
                <p className="label-mono text-faint">{p.name}</p>
                <p className="mt-2 font-display text-3xl font-bold">{p.price_display}</p>
                <p className="mt-2 text-sm text-muted min-h-[40px]">{p.description}</p>
                <button onClick={() => checkout(p.key)} disabled={disabled} data-testid={`checkout-${p.key}`}
                  className="mt-4 w-full rounded-full bg-azure py-2.5 text-sm font-medium text-white hover:bg-white hover:text-black transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-azure disabled:hover:text-white">
                  {busy === p.key ? "Redirecting…" : label}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {access?.plan === "annual" && access?.active && pricing?.upgrade?.active && (
        <div className="mt-6 rounded-2xl border border-emerald/30 bg-emerald/5 p-6">
          <p className="font-display font-bold text-lg">Upgrade to Lifetime</p>
          <p className="text-sm text-muted mt-1">Pay the ${pricing.upgrade.amount.toLocaleString()} difference. Your annual payment history is preserved.</p>
          <button onClick={() => checkout("upgrade")} disabled={busy === "upgrade"} data-testid="checkout-upgrade"
            className="mt-4 rounded-full bg-emerald px-6 py-2.5 text-sm font-medium text-black hover:bg-white transition-colors duration-200">
            {busy === "upgrade" ? "Redirecting…" : "Upgrade now"}
          </button>
        </div>
      )}

      <div className="mt-8 flex flex-col gap-3 border-t border-white/10 pt-6">
        <button onClick={openPortal} data-testid="billing-portal-btn"
          className="self-start rounded-full border border-white/15 px-5 py-2.5 text-sm font-medium text-ink hover:bg-white hover:text-black transition-colors duration-200">
          Manage billing &amp; invoices
        </button>
        <p className="text-xs text-faint max-w-xl leading-relaxed" data-testid="billing-legal-disclosure">
          Team Assembly is a product of Talent Junction, Inc. Payments and invoices are processed and issued by Talent Junction, Inc. At checkout you can enter your legal company name, billing address and tax ID (VAT / EIN).
        </p>
      </div>
    </div>
  );
}
